import reserhubApi from './reserhub-api/reserhubApi';

interface InstallmentPlan {
  card: 'amex' | 'visaMc';
  fee: number;
  interest: number;
  interestByMonth: number;
  monthlyPayment: number;
  months: number;
  total: number;
}

interface ValidateBasisTheoryInstallmentsOptions {
  bin: string;
  processor: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPositiveNumber(value: unknown): number | undefined {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? number : undefined;
}

function readNonNegativeNumber(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

function getInstallmentCardFamily(cardBrand: string): InstallmentPlan['card'] {
  const normalizedBrand = cardBrand.toLowerCase().replace(/[\s_-]/g, '');
  return normalizedBrand === 'amex' || normalizedBrand === 'americanexpress' ? 'amex' : 'visaMc';
}

/** Normalize only the active card family's usable installment plans from Purchase. */
function normalizeInstallmentPlans(paymentPlans: unknown, cardBrand: string): InstallmentPlan[] {
  if (!isRecord(paymentPlans)) return [];
  const card = getInstallmentCardFamily(cardBrand);
  const familyPlans = paymentPlans[card];
  if (!isRecord(familyPlans)) return [];

  return Object.entries(familyPlans)
    .flatMap(([monthsValue, value]) => {
      const months = Number(monthsValue);
      if (!Number.isInteger(months) || months <= 1 || !isRecord(value)) return [];

      const monthlyPayment = readPositiveNumber(value.monthlyPayment ?? value.monthly_payment);
      if (!monthlyPayment) return [];
      const total = readPositiveNumber(value.total) ?? months * monthlyPayment;

      return [
        {
          card,
          fee: readNonNegativeNumber(value.fee),
          interest: readNonNegativeNumber(value.interest),
          interestByMonth: readNonNegativeNumber(value.interestByMonth ?? value.interest_by_month),
          monthlyPayment,
          months,
          total,
        },
      ];
    })
    .sort((left, right) => left.months - right.months);
}

/** Validate a public BIN against the configured Basis Theory payment processor. */
async function validateBasisTheoryInstallments({
  bin,
  processor,
}: ValidateBasisTheoryInstallmentsOptions): Promise<boolean> {
  const normalizedProcessor = processor.trim();
  if (!/^\d{6}$/.test(bin) || !normalizedProcessor) {
    throw new Error('installments_validation_input_invalid');
  }

  const response: unknown = await reserhubApi.request(
    `/commerce/payment/${encodeURIComponent(normalizedProcessor)}/installments/validate`,
    { method: 'POST', body: JSON.stringify({ bin }) },
  );
  if (!isRecord(response) || typeof response.eligible !== 'boolean') {
    throw new Error('installments_validation_response_invalid');
  }

  return response.eligible;
}

export type { InstallmentPlan };
export { getInstallmentCardFamily, normalizeInstallmentPlans, validateBasisTheoryInstallments };
